/**
 * Pre-hearing readiness presentation (story 959). Readiness is consumed only from the platform
 * readiness gate / schedule-facing projection (dependency 912); this module never infers readiness
 * from local UI data. When the projection omits readiness fields, the display is a neutral
 * "unavailable" state that does not mark a candidate as ready or blocked.
 */

/** Provisional readiness fields carried on the 912 projection rows / proceeding detail. */
export interface ProjectionPreHearingReadiness {
  readinessStatusCode?: string | null;
  missingRequiredCount?: number | null;
  topBlockingReason?: string | null;
  lastReadinessUpdateUtc?: string | null;
  checklistCompletedCount?: number | null;
  checklistTotalCount?: number | null;
  attestationSummary?: string | null;
  overrideSummary?: string | null;
  /** Preserved for traceability when an override carries the work forward. */
  originalBlockingReason?: string | null;
}

export const PRE_HEARING_READINESS_STATUS = {
  ready: "ready",
  blocked: "blocked",
  overridePending: "override_pending",
  overrideApproved: "override_approved",
  unavailable: "unavailable",
} as const;

export type ReadinessTone = "success" | "danger" | "warning" | "active" | "muted";

/** View model the readiness components bind to. */
export interface PreHearingReadinessDisplay {
  statusCode: string;
  label: string;
  tone: ReadinessTone;
  isUnavailable: boolean;
  missingRequiredCount: number | null;
  topBlockingReason: string | null;
  checklistProgressLabel: string | null;
  lastUpdatedUtc: string | null;
  attestationSummary: string | null;
  overrideSummary: string | null;
  originalBlockingReason: string | null;
}

const STATUS_PRESENTATION: Record<string, { label: string; tone: ReadinessTone }> = {
  [PRE_HEARING_READINESS_STATUS.ready]: { label: "Ready", tone: "success" },
  [PRE_HEARING_READINESS_STATUS.blocked]: { label: "Blocked", tone: "danger" },
  [PRE_HEARING_READINESS_STATUS.overridePending]: { label: "Override Pending", tone: "warning" },
  [PRE_HEARING_READINESS_STATUS.overrideApproved]: { label: "Override Approved", tone: "active" },
};

/**
 * Maps the provisional projection readiness into a display model. Returns the neutral unavailable
 * state when readiness is absent or carries an unrecognized status, never inferring readiness.
 */
export function mapPreHearingReadiness(
  readiness: ProjectionPreHearingReadiness | null | undefined,
): PreHearingReadinessDisplay {
  const statusCode = normalizeStatus(readiness?.readinessStatusCode);
  const presentation = statusCode ? STATUS_PRESENTATION[statusCode] : undefined;

  if (!presentation) {
    return {
      statusCode: PRE_HEARING_READINESS_STATUS.unavailable,
      label: "Readiness Unavailable",
      tone: "muted",
      isUnavailable: true,
      missingRequiredCount: null,
      topBlockingReason: null,
      checklistProgressLabel: null,
      lastUpdatedUtc: null,
      attestationSummary: null,
      overrideSummary: null,
      originalBlockingReason: null,
    };
  }

  return {
    statusCode,
    label: presentation.label,
    tone: presentation.tone,
    isUnavailable: false,
    missingRequiredCount: typeof readiness?.missingRequiredCount === "number" ? readiness.missingRequiredCount : null,
    topBlockingReason: cleanText(readiness?.topBlockingReason),
    checklistProgressLabel: formatChecklistProgress(readiness?.checklistCompletedCount, readiness?.checklistTotalCount),
    lastUpdatedUtc: cleanText(readiness?.lastReadinessUpdateUtc),
    attestationSummary: cleanText(readiness?.attestationSummary),
    overrideSummary: cleanText(readiness?.overrideSummary),
    originalBlockingReason: cleanText(readiness?.originalBlockingReason),
  };
}

function normalizeStatus(statusCode: string | null | undefined): string {
  return (statusCode ?? "").trim().toLowerCase();
}

function formatChecklistProgress(completed: number | null | undefined, total: number | null | undefined): string | null {
  if (typeof completed === "number" && typeof total === "number" && total > 0) {
    return `${completed} / ${total}`;
  }
  return null;
}

function cleanText(value: string | null | undefined): string | null {
  const normalized = (value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}
